import React from 'react';
import { clsx } from 'clsx';
import {
  ClipboardDocumentIcon,
  PresentationChartLineIcon,
  ShieldCheckIcon,
  CalendarDaysIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

import type { PositionDetail } from '@/types';
import { getRateLabel, formatTradeReason } from '@/types';
import { formatDate, pluralizeUnit } from '@/utils/format';
import { formatDecimalString, toDecimalString } from '@/utils/decimal-display';
import { effectiveRates } from '@/services/trades/effective-rate';
import { pricedByPool } from '@/utils/trades/cash-pool-rate';
import { Field as UiField } from '@/components/ui/Field';
import { showsClosingCash } from '@/utils/trades/closing-cash';
import { useCounterparties } from '@/hooks/counterparty/use-counterparties';
import { useToastStore } from '@/stores';

import { resolveCreditRating, resolveMaturityDisplay } from './trade-terms-helpers';
import { isTerminalStatus } from '../analytics/analytics-economics';

/**
 * Trade Terms tab (Phase-15 redesign) — the "what we agreed" contract sheet, laid
 * out in the prototype's TWO columns: a left "deal facts" card (Agreement /
 * Security / Counterparty / Quantity & Value) and a right column of separate
 * cards with icon headers (Rate definition / Collateral / Settlement / Term &
 * dates). Honest empty states throughout.
 *
 * Eight semantic sections, headings preserved exactly as tests assert (TERMS-01):
 *   Agreement & Direction · Security · Counterparty · Quantity & Value ·
 *   Rate definition · [Collateral — conditional non-cash] · Settlement · Term & dates
 * The left card renders in the DOM before the right column, so the level-3
 * heading ORDER is unchanged by the two-column split.
 *
 * The rate is shown as a step build-up (BENCHMARK + spread = rate), mirroring the
 * Economics RateWaterfall but inline (no $/day connector, no sub-header) to match
 * the prototype's terms card; an honest "Rate not set" replaces it when there is
 * no resolved rate. Credit rating stays a three-state FE-join, never traffic-lit;
 * no HON-03 float re-derivation, no fabricated fields (no CUSIP / Minimum Fee).
 */

function upper(v?: string | null): string | undefined {
  return v ? v.toUpperCase() : undefined;
}

/**
 * Per-leg settle status. There are TWO independent facts in play:
 *
 *  1. The contract settlement "value-date" — when cash + securities legally
 *     change hands per the contract. This is `settlement_date`
 *     (open_settlement_date), the SAME source the "Settlement Date" field and
 *     the days-live KPI read.
 *
 *  2. The per-leg ACTION timestamp — when an operator clicked Settle Cash /
 *     Settle Securities. Backend writes `cash_settled_at = timezone.now()` in
 *     settlement.py — an OPERATIONAL timestamp, not the contract value-date.
 *
 * Resolution (LR-417 §B, value-date canonical): the status leads with the
 * VALUE-DATE so this card, the Settlement Date field, and the days-live KPI all
 * read one settlement date (the live-audit TRD-55 showed three different dates
 * on one page when the status led with the action timestamp). The per-leg
 * action timestamp is operational metadata: appended as "· posted {date}" only
 * when it differs from the value-date (so split-FOP / late settles stay honest)
 * — never the headline date. The raw action time still anchors the Activity
 * audit timeline. Falls back to actual_settlement_date then the action
 * timestamp when settlement_date is missing; all-null → "Settled (date
 * unavailable)" preserves WR-03 (no bare em-dash).
 */
/**
 * Truncate an ISO datetime to its UTC date prefix (`YYYY-MM-DD`) so the per-leg
 * action timestamp formats consistently with the contract date fields beside
 * it on the same card. `formatDate` parses `YYYY-MM-DD` as UTC midnight, but
 * `new Date('2026-05-22T20:00:00Z').toLocaleDateString(...)` is local-TZ; on a
 * NY desk a UTC-evening click stamp could render as the NEXT or PRIOR day vs
 * the date-only `settlement_date` field, reintroducing the audit-N8 dual-date
 * footgun in a locale-dependent form CI (UTC) never sees.
 */
function toUtcDateOnly(value: string): string {
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(value);
  return match ? match[1] : value;
}

function resolveLegStatus(
  settled: boolean | undefined,
  legSettledAt: string | null | undefined,
  actualSettlementDate: string | null | undefined,
  settlementDate: string | null | undefined,
): string {
  if (!settled) return 'Pending';
  // Value-date canonical: the headline is the contract settlement (value) date —
  // the SAME source as the Settlement Date field + days-live KPI. actual_/leg
  // are defensive fallbacks only (LR-417 §B).
  const valueDate = settlementDate ?? actualSettlementDate ?? null;
  if (valueDate == null) {
    return legSettledAt != null
      ? `Settled ${formatDate(toUtcDateOnly(legSettledAt))}`
      : 'Settled (date unavailable)';
  }
  const base = `Settled ${formatDate(toUtcDateOnly(valueDate))}`;
  // Action timestamp is operational metadata — shown only when it differs from
  // the value-date, labeled "posted", never as the headline date.
  if (legSettledAt != null && toUtcDateOnly(legSettledAt) !== toUtcDateOnly(valueDate)) {
    return `${base} · posted ${formatDate(toUtcDateOnly(legSettledAt))}`;
  }
  return base;
}

/** Left-card section heading — uppercase muted title with a hairline underline (R8). */
function SectionHeading({ id, title }: { id: string; title: string }) {
  return (
    <h3
      id={id}
      className="mb-4 border-b border-lr-border pb-3 text-[11px] font-bold uppercase tracking-[0.8px] text-lr-muted"
    >
      {title}
    </h3>
  );
}

/** Right-column card header — leading icon + semibold h3 (prototype `.card-h`). The
 *  title stays an <h3> (not a plain span) so the level-3 heading contract holds. */
function CardHeader({ id, title, icon }: { id: string; title: string; icon: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 border-b border-lr-border px-[18px] py-[13px]">
      {icon}
      <h3 id={id} className="text-[13.5px] font-semibold text-lr-fg">
        {title}
      </h3>
    </div>
  );
}

/** A right-column card: section wrapper + bordered surface + icon header. Children
 *  control their own padding (the rate card is full-bleed above its field grid). */
function TermsCard({
  id,
  title,
  icon,
  children,
}: {
  id: string;
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section aria-labelledby={id}>
      <div className="overflow-hidden rounded-[10px] border border-lr-border bg-lr-surface">
        <CardHeader id={id} title={title} icon={icon} />
        {children}
      </div>
    </section>
  );
}

/**
 * Local alias for the shared `<Field>` primitive bound to the `<dl>` /
 * `<dt>` / `<dd>` semantic this card uses throughout (LR-208 r3 L2 — formerly
 * an inline duplicate of the shared component; the two now share one source
 * with the definition-list mode opted in once at this call site).
 *
 * The `size`-prop name comes from the shared API; the legacy `big` boolean
 * has been mapped at each call site below.
 */
function Field(
  props: Omit<React.ComponentProps<typeof UiField>, 'as'>,
) {
  return <UiField {...props} as="definition" />;
}

/** Inline credit-rating chip — neutral, three-state, never traffic-lit. */
function CreditRatingChip({ counterpartyName }: { counterpartyName: string }) {
  // LR-661: ask for THIS counterparty. Unnarrowed this was one 100-row page joined
  // by exact name, so on a large registry only the alphabetical head resolved.
  // `?search=` is icontains, hence the exact pick below.
  const enabled = !!counterpartyName;
  const { data, isError } = useCounterparties(
    // Explicit limit: the read defaults to one 100-row page, and a name that is a
    // substring of more rows than that would push its own exact match off it.
    { search: counterpartyName, limit: 500 },
    { enabled },
  );
  // `[]` while off, not `undefined`: resolveCreditRating reads undefined as "loading"
  // before it looks at the empty name, and would strand the pulse forever.
  const result = resolveCreditRating(counterpartyName, enabled ? data : [], isError);

  if (result.state === 'loading') {
    return (
      <span
        role="status"
        className="inline-block h-5 w-12 animate-pulse rounded-full bg-gray-100"
        aria-busy="true"
        aria-label="Loading credit rating"
      />
    );
  }
  if (result.state === 'present') {
    return (
      <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
        Rating: {result.rating}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium text-lr-faint">
      Rating: not available
    </span>
  );
}

interface WfStep {
  op: string | null;
  label: string;
  value: string;
  tone?: 'result' | 'neg';
}

/**
 * Inline rate build-up steps (prototype terms `.waterfall`). The rate as a visual
 * formula: BENCHMARK + spread = rate for floating rebate, a single resolved step
 * otherwise. Mirrors the Economics RateWaterfall's convention (LR-464: the operator
 * reflects the spread's sign — positive adds "+", negative subtracts "−") but
 * WITHOUT its Panel sub-header or $/day
 * connector, matching the prototype's terms card. Display-only: the authoritative
 * figure is total_rate; every value is formatted via the Decimal helper (HON-03).
 * Only rendered when a rate is resolved — the caller shows "Rate not set" otherwise.
 */
function RateBuildUp({ trade }: { trade: PositionDetail }) {
  // LR-628: same effective-rate rule as the Analytics tab's RateWaterfall — the
  // two tabs must never format the same field differently.
  const { benchmarkRate, totalRate } = effectiveRates(trade);
  const rateType = trade.rate_type || 'fixed';
  const isFloatingRebate = rateType === 'floating' && trade.rate_entry_mode === 'rebate';
  const isFeeMode = trade.rate_entry_mode === 'fee';
  const benchmarkUpper = (trade.benchmark_name || '').toUpperCase();
  const spreadBps = trade.spread_bps ?? 0;
  const rateStr = `${formatDecimalString(totalRate, { dp: 2 })}%`;
  const hasFeePercent = toDecimalString(trade.fee_percent) != null;
  const feeStr = hasFeePercent ? `${formatDecimalString(trade.fee_percent, { dp: 2 })}%` : '—';
  // Guard the percent suffix when benchmark_rate is null — otherwise
  // `formatDecimalString(null)` returns '—' and the result reads as the
  // malformed string '—%' (mirrors the RateWaterfall guard in the sibling
  // Analytics tab; the two tabs must format the same field the same way).
  const hasBenchmark = toDecimalString(benchmarkRate) != null;
  const benchmarkStr = hasBenchmark ? `${formatDecimalString(benchmarkRate, { dp: 2 })}%` : '—';

  const steps: WfStep[] = isFeeMode
    ? [
        // Fee mode: no single resolved rate — fee on par and cost on CV use
        // different bases (feedback §2b/d, prior audit F8). Show the two legs
        // side-by-side instead of a netted total_rate that read as nonsense
        // (e.g. "Fixed Rate −1.30%" for fee 4% − rebate 5.30%).
        { op: null, label: 'Fee · on gross', value: feeStr, tone: 'result' },
        {
          op: null,
          // LR-631: on a pooled fee trade this figure is the POOL's rate -
          // benchmark PLUS the pool's spread, or a flat fixed-pool rate - and
          // the booking snapshot carries the pool's benchmark NAME. Labelling it
          // "Cost · SOFR" would have the platform quoting SOFR at a value the
          // Benchmarks page contradicts, and a fixed pool would read
          // "Cost · OTHER". Name the pool, which is what the figure is.
          label: pricedByPool(trade)
            ? `Cost · ${trade.cash_pool_name || 'cash pool'}`
            : benchmarkUpper ? `Cost · ${benchmarkUpper}` : 'Cost · benchmark',
          value: benchmarkStr,
          tone: 'neg',
        },
      ]
    : isFloatingRebate && benchmarkUpper
      ? [
          {
            op: null,
            label: `Benchmark · ${benchmarkUpper}`,
            value: benchmarkStr,
          },
          // Rebate convention (LR-464): rebate = benchmark + spread. The operator
          // reflects the spread's sign — a positive spread adds ("+"), a
          // special-stock NEGATIVE spread subtracts ("−").
          { op: spreadBps >= 0 ? '+' : '−', label: 'Spread', value: `${Math.abs(spreadBps)} bps`, tone: spreadBps < 0 ? 'neg' : undefined },
          { op: '=', label: getRateLabel(trade.trade_type), value: rateStr, tone: 'result' },
        ]
      : [
          {
            op: null,
            label: trade.rate_type_display ? `${trade.rate_type_display} rate` : 'Rate',
            value: rateStr,
            tone: 'result',
          },
        ];

  return (
    <div className="flex flex-wrap items-stretch border-b border-lr-border">
      {steps.map((s, i) => (
        <div
          key={s.label}
          className={clsx(
            'relative min-w-[120px] flex-1 px-[18px] py-4',
            i > 0 && 'border-l border-lr-border',
            s.tone === 'result' && 'bg-lr-accent-weak',
          )}
        >
          {s.op && (
            <div className="absolute -left-3 top-1/2 z-[2] grid h-6 w-6 -translate-y-1/2 place-items-center rounded-full border border-lr-border-2 bg-lr-surface text-sm font-bold text-lr-muted">
              {s.op}
            </div>
          )}
          <div className="text-[11px] font-semibold uppercase tracking-[0.5px] text-lr-muted">
            {s.label}
          </div>
          <div
            className={clsx(
              'lr-num mt-1.5 text-[22px] font-semibold tracking-[-0.4px]',
              s.tone === 'result' ? 'text-lr-accent' : s.tone === 'neg' ? 'text-lr-neg' : 'text-lr-fg',
            )}
          >
            {s.value}
          </div>
        </div>
      ))}
    </div>
  );
}

const gridLeft = 'grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-4';
const gridRight = 'grid grid-cols-2 gap-x-6 gap-y-4';
const leftSep =
  'space-y-6 [&>section+section]:border-t [&>section+section]:border-lr-border [&>section+section]:pt-6';

export function TradeTermsTab({ trade }: { trade: PositionDetail }) {
  const { addToast } = useToastStore();

  const handleCopyIsin = async () => {
    try {
      await navigator.clipboard.writeText(trade.isin);
      addToast('success', 'ISIN copied to clipboard');
    } catch {
      addToast('error', 'Failed to copy ISIN');
    }
  };

  const settlementCurrency = trade.settlement_currency ?? trade.currency;
  // Cross-ccy when the settlement currency differs from the instrument's quotation
  // currency. Drives the "Settles in <ccy>" badge AND the stacked pair layout below.
  // Case-INSENSITIVE, mirroring settlement-ccy.ts `isCrossCurrency` and the r1-fixed
  // analytics `hasSettlementDailyReturn` (LR-474 r6): a non-canonical 'usd' vs 'USD'
  // payload must classify the same here as on the sibling KPI/PnL surfaces, else this
  // tab alone would show a false "Settles in" badge + a spurious FX Rate row. Requiring
  // a present settlement_currency keeps a same-ccy/legacy trade out of the cross-ccy path.
  const isCrossCcy =
    !!trade.settlement_currency &&
    !!trade.currency &&
    trade.settlement_currency.toUpperCase() !== trade.currency.toUpperCase();
  // LR-494: booked FX rate also displayed as the EUR/USD market pair, to match
  // the COB FX Rate field below. The pair value is 1/(stored security→settlement
  // rate); this reciprocal is float division, but the value is display-only (not
  // an authoritative money figure) and formatDecimalString renders it at a clean
  // 6dp, so the float artefact never reaches the screen.
  const bookedFxNum = trade.fx_rate != null ? Number(trade.fx_rate) : NaN;
  const bookedFxDisplay = Number.isFinite(bookedFxNum) && bookedFxNum > 0 ? 1 / bookedFxNum : null;
  const showFxRate = isCrossCcy && bookedFxDisplay != null;
  // LR-494: COB (T-1 close) FX rate, security → settlement (the COB twin of the
  // booked fx_rate). `trade` is the wire shape here (decimals are strings), so
  // coerce and gate on a positive number — matching the blotter column's `> 0`
  // check (kvi #9a): a "0"/non-positive wire value falls back to the booked rate
  // rather than truthy-passing and then hiding the field. Falls back to the
  // booked rate when TickDB has no COB FX (early days / unsupported pair).
  const cobFxRaw = trade.cob_fx_rate != null ? Number(trade.cob_fx_rate) : NaN;
  const cobFxUsable = Number.isFinite(cobFxRaw) && cobFxRaw > 0;
  const cobFxSource = cobFxUsable ? trade.cob_fx_rate : (trade.fx_rate ?? null); // security → settlement
  // Display the market pair settlement/security ("EUR/USD") — the inverse of the
  // stored security→settlement rate (display-only reciprocal, see bookedFx note).
  const cobFxNum = cobFxSource != null ? Number(cobFxSource) : NaN;
  const cobFxDisplay = Number.isFinite(cobFxNum) && cobFxNum > 0 ? 1 / cobFxNum : null;
  const showCobFxRate = isCrossCcy && cobFxDisplay != null;
  const fxPairLabel = `${(settlementCurrency || '').toUpperCase()}/${(trade.currency || '').toUpperCase()}`;

  // The security-ccy original, rendered as the quiet sub-line beneath each
  // settlement-ccy primary in a cross-ccy pair (LR-474 r5). The currency is shown
  // ONCE, as a faint trailing ISO tag — the digits carry no currency symbol, so the
  // line reads "1,234.56 USD", not the redundant "$1,234.56 USD" a symbol+code pair
  // produced (LR-474 r5 #6). This also mirrors how the settlement primary above
  // annotates its currency once, via the "(ccy)" sublabel on its label.
  const securitySubline = (value: string | number | null | undefined): React.ReactNode =>
    toDecimalString(value) == null ? undefined : (
      <>
        {formatDecimalString(value, { dp: 2 })}
        {trade.currency && <span className="ml-1 text-lr-faint">{trade.currency}</span>}
      </>
    );

  const maturity = resolveMaturityDisplay(trade.term_type, trade.maturity_date, trade.notice_served);

  // Days-to-maturity (Phase-15 redesign moved this from Economics → Trade Terms;
  // Economics no longer carries days-active/days-to-maturity). String() coerces
  // the live-backend case where the string-typed field arrives as a JS number; a
  // terminal trade shows "—", an open term "Open — no maturity".
  //
  // Terminal set is uniform across the page (KpiStrip / StickySummary / PnlTable /
  // RateWaterfall / AnalyticsTab): {closed, cancelled, matured}. Audit N1/N9/N10
  // treats matured as conceptually-closed; the days-to-maturity cell follows the
  // same doctrine to avoid drift on the same screen (label "Matured" would still
  // fall through the dtmNum ≤ 0 branch on a recently-matured trade — but a
  // long-matured / re-rolled / split-settle row should read "—" like the rest of
  // the page, not a stale day count). Single source:
  // analytics-economics.TERMINAL_STATUSES (LR-208 r5 M3).
  const isTerminal = isTerminalStatus(trade.status);
  const isOpenTerm = trade.term_type === 'open' || !trade.maturity_date;
  const dtmNum = Number.parseInt(String(trade.days_to_maturity ?? '').trim(), 10);
  const daysToMaturity = isTerminal
    ? '—'
    : isOpenTerm
      ? 'Open — no maturity'
      : Number.isFinite(dtmNum)
        ? dtmNum <= 0
          ? 'Matured'
          : pluralizeUnit(dtmNum, 'day')
        : '—';

  const hasSettlementDate = !!trade.settlement_date;
  // LR-628: the terms grid quotes the same effective benchmark the build-up above
  // it does — one card must not carry two different numbers for one field.
  const { benchmarkRate: benchmarkRateField, totalRate, showingLiveRate } =
    effectiveRates(trade);
  const hasRate = toDecimalString(totalRate) != null;
  // Closing cash is computed for every term type (Open/Evergreen projected to
  // today via closing_cash_projected), so surface it whenever a value exists
  // (LR-414·E / feedback §2g.ii). toDecimalString guards the live-backend case
  // where the string-typed field arrives as a JS number.
  const hasClosingCash = toDecimalString(trade.closing_cash) != null;
  const rateEntryMode = trade.rate_entry_mode;
  // LR-631: a fee trade whose cash sits in a pool - the shape whose benchmark
  // fields describe the POOL rather than an index. `pricedByPool` carries the
  // backend's own exclusions, so a trade the engine does NOT price off its pool
  // is not labelled as if it were (dp review, Low 2).
  const isPooledFee = pricedByPool(trade);

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1.15fr_1fr] lg:items-start">
      {/* LEFT COLUMN — deal facts in one card */}
      <div className={clsx('rounded-[10px] border border-lr-border bg-lr-surface p-6', leftSep)}>
        {/* 1. Agreement & Direction */}
        <section aria-labelledby="terms-agreement-h">
          <SectionHeading id="terms-agreement-h" title="Agreement & Direction" />
          <dl className={gridLeft}>
            <Field label="Agreement" value={trade.agreement_display ?? upper(trade.agreement)} />
            <Field label="Trade Type" value={trade.trade_type_display ?? trade.trade_type} />
            {trade.trade_reason && (
              <Field label="Trade Reason" value={formatTradeReason(trade.trade_reason)} />
            )}
          </dl>
        </section>

        {/* 2. Security */}
        <section aria-labelledby="terms-security-h">
          <div className="mb-3 flex items-center justify-between">
            <SectionHeading id="terms-security-h" title="Security" />
            <button
              type="button"
              onClick={handleCopyIsin}
              className="inline-flex items-center gap-1 text-xs text-lr-accent hover:underline"
            >
              <ClipboardDocumentIcon className="h-3.5 w-3.5" aria-hidden={true} />
              Copy ISIN
            </button>
          </div>
          <div className="mb-4">
            <span className="font-mono text-xl font-bold text-lr-fg">{trade.symbol_id}</span>{' '}
            <span className="text-sm text-lr-muted">{trade.description}</span>
          </div>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
            <Field label="ISIN" value={trade.isin} mono title={trade.isin} />
            {trade.sedol && <Field label="SEDOL" value={trade.sedol} mono />}
            {trade.security_type && <Field label="Type" value={upper(trade.security_type)} />}
            {trade.country && <Field label="Country" value={upper(trade.country)} />}
            {/* LR-474: disambiguated from the settlement currency shown in the
                Settlement card — this is the instrument's quotation currency. */}
            <Field label="Security Currency" value={trade.currency} />
          </dl>
        </section>

        {/* 3. Counterparty */}
        <section aria-labelledby="terms-counterparty-h">
          <SectionHeading id="terms-counterparty-h" title="Counterparty" />
          <div className="mb-4 flex items-center gap-3">
            <span className="text-lg font-bold text-lr-fg">{trade.counterparty_name}</span>
            <CreditRatingChip counterpartyName={trade.counterparty_name} />
          </div>
          <dl className={gridLeft}>
            {trade.custodian_account && (
              <Field label="Our Account" sublabel="(Custodian)" value={trade.custodian_account} mono />
            )}
            {trade.counterparty_account && (
              <Field label="Their Account" sublabel="(Counterparty)" value={trade.counterparty_account} mono />
            )}
            {trade.booking_center && <Field label="Booking Center" value={trade.booking_center} />}
          </dl>
        </section>

        {/* 4. Quantity & Value */}
        <section aria-labelledby="terms-quantity-h">
          {/* LR-474 r5: the settlement currency drives this whole block. Badge it
              on the heading (cross-ccy only) and lead every cross-ccy pair with the
              settlement figure — the security-ccy original is demoted to a quiet
              sub-line below. The <h3> text stays exactly "Quantity & Value" so the
              level-3 heading-order contract (TERMS-01) is unchanged; the badge is a
              sibling <span>. The border lives on the flex row so the heading hairline
              still spans full width. */}
          <div className="mb-4 flex items-center justify-between gap-3 border-b border-lr-border pb-3">
            <h3
              id="terms-quantity-h"
              className="text-[11px] font-bold uppercase tracking-[0.8px] text-lr-muted"
            >
              Quantity & Value
            </h3>
            {isCrossCcy && (
              <span className="inline-flex flex-none items-center rounded-full bg-lr-accent-weak px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.4px] text-lr-accent">
                Settles in {settlementCurrency}
              </span>
            )}
          </div>
          <dl className={gridLeft}>
            <Field label="Quantity" value={trade.quantity.toLocaleString()} size="big" />
            <Field
              label="Gross Price"
              value={formatDecimalString(trade.gross_price, { currency: trade.currency, dp: 2 })}
              title="Quoted price before the margin adjustment (gross). The margin-adjusted Margin Price × quantity gives Contract Value."
              mono
            />
            {/* LR-474 r5: each cross-ccy pair is ONE stacked Field — the trader chose
                the settlement ccy, so its figure is the bold primary and the
                security-ccy original sits beneath as the quiet `securitySubline`.
                This fixes the prior dual-`size=big` ambiguity (EUR vs USD read at
                equal weight) AND the 3-col wrap that split a pair across rows.
                Same-ccy trades have no converted twin → render the single native
                Field unchanged. */}
            {/* LR-522: the booking (Trade-stage) Contract Value (qty × margin_price).
                The marked twins are null until the first mark (LR-522), so this row
                must read the canonical booking fields — otherwise a never-marked
                trade / repo shows nothing. The Last Marked figure has its own
                blotter column and is intentionally not surfaced on this tab. */}
            {toDecimalString(trade.contract_value_converted) != null ? (
              <Field
                label="Contract Value"
                sublabel={trade.settlement_currency ? `(${trade.settlement_currency})` : undefined}
                value={formatDecimalString(trade.contract_value_converted, {
                  currency: trade.settlement_currency || undefined,
                  dp: 2,
                })}
                size="big"
                title="Booked contract value, in the settlement currency (converted at the trade FX rate); the security-currency original is shown beneath."
                secondary={securitySubline(trade.contract_value)}
              />
            ) : (
              trade.contract_value && (
                <Field
                  label="Contract Value"
                  value={formatDecimalString(trade.contract_value, { currency: trade.currency, dp: 2 })}
                  size="big"
                />
              )
            )}
            {/* Margin Price pair — the settlement primary is bumped to 'mid' (a notch
                below the million-unit totals: it's a per-share price, so 'big' would
                shout) so it still clearly leads its security-ccy original beneath. */}
            {/* LR-522: the booking (Trade-stage) Margin Price (gross_price × margin).
                Reads the canonical booking fields, not the null-until-marked twins. */}
            {toDecimalString(trade.margin_price_converted) != null ? (
              <Field
                label="Margin Price"
                sublabel={trade.settlement_currency ? `(${trade.settlement_currency})` : undefined}
                value={formatDecimalString(trade.margin_price_converted, {
                  currency: trade.settlement_currency || undefined,
                  dp: 2,
                })}
                size="mid"
                title="Booked margin price, in the settlement currency (converted at the trade FX rate); the security-currency original is shown beneath."
                secondary={securitySubline(trade.margin_price)}
              />
            ) : (
              trade.margin_price && (
                <Field
                  label="Margin Price"
                  value={formatDecimalString(trade.margin_price, { currency: trade.currency, dp: 2 })}
                  mono
                />
              )
            )}
            <Field
              label="Margin"
              value={`${formatDecimalString(trade.margin, { dp: 2 }).replace(/\.?0+$/, '')}%`}
              mono
            />
            {/* Closing Cash pair — the settlement primary CARRIES the title +
                'projected' hint (they describe the headline settlement figure);
                the security-ccy original is the quiet sub-line beneath. */}
            {showsClosingCash(trade) &&
              (toDecimalString(trade.closing_cash_converted) != null ? (
                <Field
                  label="Closing Cash"
                  sublabel={
                    trade.settlement_currency ? `(${trade.settlement_currency})` : undefined
                  }
                  value={formatDecimalString(trade.closing_cash_converted, {
                    currency: trade.settlement_currency || undefined,
                    dp: 2,
                  })}
                  size="big"
                  title="Cash leg expected at close: contract value plus accrued interest (GMRA), in the settlement currency (converted at the trade FX rate)."
                  hint={
                    trade.closing_cash_projected
                      ? 'projected to today (no fixed maturity)'
                      : undefined
                  }
                  secondary={hasClosingCash ? securitySubline(trade.closing_cash) : undefined}
                />
              ) : (
                hasClosingCash && (
                  <Field
                    label="Closing Cash"
                    value={formatDecimalString(trade.closing_cash, {
                      currency: trade.currency,
                      dp: 2,
                    })}
                    size="big"
                    title="Cash leg expected at close: contract value plus accrued interest (GMRA)."
                    hint={
                      // LR-208 r6 M15: this same-ccy branch owns the hint; on a
                      // cross-ccy pair it lives on the primary above.
                      trade.closing_cash_projected
                        ? 'projected to today (no fixed maturity)'
                        : undefined
                    }
                  />
                )
              ))}
          </dl>
          <p className="mt-3 text-xs text-lr-muted">
            Margin: collateral required as a percentage of contract value (e.g. 102% = 2%
            over-collateralisation).
          </p>
        </section>
      </div>

      {/* RIGHT COLUMN — Rate / Collateral / Settlement / Term, separate cards */}
      <div className="space-y-5">
        {/* 5. Rate definition — step build-up (or honest "Rate not set") + field grid */}
        <TermsCard
          id="terms-rate-h"
          title="Rate definition"
          icon={<PresentationChartLineIcon className="h-[15px] w-[15px] flex-none text-lr-muted" aria-hidden={true} />}
        >
          {hasRate ? (
            <RateBuildUp trade={trade} />
          ) : (
            <div className="border-b border-lr-border px-[18px] py-5 text-sm italic text-lr-faint">
              Rate not set
            </div>
          )}
          <div className="p-[18px]">
            <dl className={gridRight}>
              <Field label="Rate Type" value={trade.rate_type_display ?? trade.rate_type ?? '—'} />
              {rateEntryMode && <Field label="Rate Entry Mode" value={upper(rateEntryMode)} />}
              {/* LR-631: a pooled fee trade's `benchmark_name` is the POOL's, and
                  its `benchmark_rate` is that benchmark PLUS the pool's spread -
                  so printing them as "Benchmark / Benchmark Rate" quotes an index
                  at a value that is not that index's. Name the source instead. */}
              {isPooledFee ? (
                <Field label="Rate source" value={trade.cash_pool_name || 'Cash pool'} />
              ) : (
                trade.benchmark_name && <Field label="Benchmark" value={upper(trade.benchmark_name)} />
              )}
              {/* `!= null`, not truthiness: a rate of exactly 0 is a real value and
                  must not vanish. Since LR-623 any plus-to-minus re-rate passes
                  through zero, and stopping there is legitimate. Matches the
                  spread_bps line below; note the runtime type varies (the string
                  "0.0000" is truthy, the number 0 is not). */}
              {/* LR-628: a floating trade's benchmark is the rate the accrual is
                  using now, not the number booked with the trade — say so in the
                  label, or the value silently contradicts the "Benchmark Locked:
                  No" beside it.
                  LR-656: the qualifier is "(current)", never "(today)". The
                  fixing behind it may have been carried forward for up to
                  MAX_FIXING_STALENESS_DAYS, and a pooled fee trade quotes its
                  pool's definition rather than any fixing at all — one word has
                  to be true of both, and no date is available for either. */}
              {benchmarkRateField != null && (
                <Field
                  label={
                    isPooledFee
                      ? (showingLiveRate ? 'Cash Pool Rate (current)' : 'Cash Pool Rate')
                      : showingLiveRate ? 'Benchmark Rate (current)' : 'Benchmark Rate'
                  }
                  value={`${formatDecimalString(benchmarkRateField, { dp: 2 })}%`}
                  mono
                />
              )}
              {trade.spread_bps != null && <Field label="Spread" value={`${trade.spread_bps} bps`} mono />}
              {trade.fee_percent != null && <Field label="Fee" value={`${formatDecimalString(trade.fee_percent, { dp: 2 })}%`} mono />}
              {/* LR-628: `benchmark_locked` is only ever set for a FIXED rate
                  (Position.save), so a fixing-driven trade reads "Not locked"
                  next to the label above — i.e. "you may set this" — while
                  re-rate answers 400 for it and the form shows no input at all.
                  Gated on `benchmark_follows_fixing`, NOT on showingLiveRate:
                  the latter goes dark when the feed does, whereas the guard keeps
                  refusing. */}
              {trade.benchmark_locked !== undefined && (
                <Field
                  label="Benchmark Locked"
                  value={
                    // LR-631: this row answers "who may move the benchmark", and
                    // once the accrual owns it the booking-time flag answers the
                    // wrong question. A pooled fee trade is booked rate_type
                    // 'fixed', so the flag says "Locked @ <trade date>" beside a
                    // rate that reprices with the pool every night; a floating
                    // rebate has the mirror defect, saying "Not locked" of a
                    // benchmark both re-rate and amend refuse. Key on ownership
                    // (`benchmark_follows_fixing`), not on the flag or on
                    // whether today's figure happens to be showing.
                    // Terminal trades excluded: the accrual has stopped setting
                    // anything on a matured or closed trade, and its sibling
                    // `effective_benchmark_rate` is already null there - the same
                    // card would otherwise show a booked rate under a caption
                    // saying the engine owns it.
                    trade.benchmark_follows_fixing && !isTerminal
                      ? 'Set by the accrual'
                      : trade.benchmark_locked
                        ? trade.benchmark_lock_date
                          // Avoid the bare "Locked @ —" when the flag is set but the
                          // date is missing (WR-03 no-bare-em-dash, LR-208 r6 LOW).
                          ? `Locked @ ${formatDate(trade.benchmark_lock_date)}`
                          : 'Locked (date unavailable)'
                        : 'Not locked'
                  }
                />
              )}
              {trade.dividend_passthrough_percent && (
                <Field
                  label="Dividend Pass-Through"
                  value={`${formatDecimalString(trade.dividend_passthrough_percent, { dp: 2 }).replace(/\.?0+$/, '')}%`}
                  mono
                />
              )}
            </dl>
          </div>
        </TermsCard>

        {/* 6. Collateral — CONDITIONAL: non-cash only (between Rate and Settlement) */}
        {trade.collateral_type === 'non_cash' && (
          <TermsCard
            id="terms-collateral-h"
            title="Collateral"
            icon={<ShieldCheckIcon className="h-[15px] w-[15px] flex-none text-lr-muted" aria-hidden={true} />}
          >
            <div className="p-[18px]">
              <dl className={gridRight}>
                <Field label="Collateral Type" value={trade.collateral_type_display ?? 'Non-Cash'} />
                {trade.collateral_symbol_id && (
                  <Field label="Collateral Security" value={trade.collateral_symbol_id} mono />
                )}
                {trade.collateral_isin && <Field label="Collateral ISIN" value={trade.collateral_isin} mono />}
                {trade.collateral_quantity != null &&
                  trade.collateral_quantity !== '' &&
                  Number.isFinite(Number(trade.collateral_quantity)) && (
                    <Field
                      label="Collateral Quantity"
                      // Decimal-safe 6dp display, trailing zeros (and a bare dot) trimmed.
                      value={formatDecimalString(trade.collateral_quantity, { dp: 6 }).replace(/\.?0+$/, '')}
                      mono
                    />
                  )}
                {trade.collateral_cob_price != null && (
                  <Field
                    label="Collateral COB Price"
                    value={formatDecimalString(trade.collateral_cob_price, { currency: trade.currency, dp: 2 })}
                    mono
                  />
                )}
                {trade.collateral_percent && (
                <Field
                  label="Collateral %"
                  value={`${formatDecimalString(trade.collateral_percent, { dp: 2 }).replace(/\.?0+$/, '')}%`}
                  mono
                />
              )}
                {trade.pre_delivery_party && (
                  <Field
                    label="Pre-Delivery Party"
                    value={trade.pre_delivery_party_display ?? upper(trade.pre_delivery_party)}
                  />
                )}
              </dl>
            </div>
          </TermsCard>
        )}

        {/* 7. Settlement — shared instructions + split legs */}
        <TermsCard
          id="terms-settlement-h"
          title="Settlement"
          icon={<CalendarDaysIcon className="h-[15px] w-[15px] flex-none text-lr-muted" aria-hidden={true} />}
        >
          <div className="p-[18px]">
            <dl className={gridRight}>
              {trade.delivery_type && <Field label="Delivery Type" value={upper(trade.delivery_type)} />}
              {trade.settlement_venue && <Field label="Settlement Venue" value={upper(trade.settlement_venue)} />}
              <Field label="Settlement Currency" value={settlementCurrency} />
              {/* LR-586: which pool holds this trade's cash collateral. Here and
                  not in the Collateral card: that card renders for `non_cash`
                  ONLY, so a Cash Pool row inside it could never appear on the
                  one kind of trade that has a pool. Absent on non-cash and on
                  anything booked before LR-586. */}
              {trade.cash_pool_name && <Field label="Cash Pool" value={trade.cash_pool_name} />}
              {/* LR-494: booked FX rate shown as the EUR/USD market pair (inverse of
                  the stored security→settlement rate), consistent with the COB FX Rate
                  field below. The 1/rate reciprocal is float division, but the value is
                  display-only and 6dp formatDecimalString rounding absorbs the artefact —
                  no authoritative money figure is derived from float here. */}
              {showFxRate && (
                <Field
                  label="FX Rate"
                  sublabel={`(${fxPairLabel})`}
                  value={formatDecimalString(bookedFxDisplay, { dp: 6 })}
                  mono
                />
              )}
              {/* LR-494: COB (T-1 close) FX rate beside the booked rate. Falls back
                  to the booked rate (title flags it) when TickDB has no COB FX. */}
              {showCobFxRate && (
                <Field
                  label="COB FX Rate"
                  sublabel={`(${fxPairLabel})`}
                  value={formatDecimalString(cobFxDisplay, { dp: 6 })}
                  title={
                    cobFxUsable
                      ? `COB (T-1 close) FX rate, ${fxPairLabel}`
                      : `Booked FX rate (COB FX rate unavailable), ${fxPairLabel}`
                  }
                  mono
                />
              )}
            </dl>
            {trade.settlement_method && (
              <p className="mt-2 text-xs text-lr-muted">Settlement method: {trade.settlement_method}</p>
            )}
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="rounded-lg bg-lr-surface-2 p-4">
                <h4 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.5px] text-lr-muted">
                  Cash Settlement
                </h4>
                <dl className="space-y-2">
                  <Field label="Place" value={trade.cash_settlement_place_name ?? '—'} />
                  <Field
                    label="Status"
                    value={resolveLegStatus(
                      trade.cash_settled,
                      trade.cash_settled_at,
                      trade.actual_settlement_date,
                      trade.settlement_date,
                    )}
                  />
                </dl>
              </div>
              <div className="rounded-lg bg-lr-surface-2 p-4">
                <h4 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.5px] text-lr-muted">
                  Securities Settlement
                </h4>
                <dl className="space-y-2">
                  <Field label="Place" value={trade.securities_settlement_place_name ?? '—'} />
                  <Field
                    label="Status"
                    value={resolveLegStatus(
                      trade.securities_settled,
                      trade.securities_settled_at,
                      trade.actual_settlement_date,
                      trade.settlement_date,
                    )}
                  />
                </dl>
              </div>
            </div>
          </div>
        </TermsCard>

        {/* 8. Term & dates */}
        <TermsCard
          id="terms-term-h"
          title="Term & dates"
          icon={<ClockIcon className="h-[15px] w-[15px] flex-none text-lr-muted" aria-hidden={true} />}
        >
          <div className="p-[18px]">
            <dl className={gridRight}>
              <Field label="Term Type" value={trade.term_type_display ?? trade.term_type ?? '—'} />
              <Field label="Trade Date" value={formatDate(trade.trade_date)} />
              <Field
                label="Settlement Date"
                empty={!hasSettlementDate}
                emptyLabel="Not set"
                hint={!hasSettlementDate ? 'open term' : undefined}
                value={formatDate(trade.settlement_date)}
              />
              <Field
                label="Maturity Date"
                value={
                  maturity.kind === 'date' ? (
                    formatDate(maturity.date)
                  ) : (
                    <span className="italic text-lr-faint">{maturity.label}</span>
                  )
                }
              />
              <Field label="Days to maturity" value={daysToMaturity} />
              {trade.notice_period_days != null && (
                <Field label="Notice Period" value={pluralizeUnit(trade.notice_period_days, 'day')} />
              )}
              {(trade.term_type === 'evergreen' || trade.notice_served) && (
                <Field
                  label="Notice Served"
                  value={
                    trade.notice_served
                      ? `Served ${formatDate(trade.notice_served_date ?? null)}`
                      : 'Not served'
                  }
                />
              )}
            </dl>
          </div>
        </TermsCard>
      </div>
    </div>
  );
}
